/**
 * Access Grant Helpers
 * 
 * Functions for managing access grants (licenses/permissions)
 * Used for checking, creating, and revoking access to jobs and evidence
 */

import { doc, setDoc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Create an access grant (license) for a user
 * 
 * @param {Object} grantData - Grant configuration
 * @param {string} grantData.userId - Who gets access
 * @param {string} grantData.jobId - What they can access
 * @param {string} grantData.evidenceId - Associated video
 * @param {string} grantData.casefileId - Source casefile
 * @param {string} grantData.grantType - 'owner' | 'request_approved' | 'purchase'
 * @param {string} [grantData.requestId] - If approved from request
 * @param {string} [grantData.approvedBy] - Who approved (if applicable)
 * @param {string} [grantData.casefileName] - For display
 * @param {string} [grantData.casefileType] - 'public' | 'private'
 * @param {boolean} [grantData.showCasefileName] - Show real name?
 * @returns {Promise<string>} Grant ID
 */
export const createAccessGrant = async (grantData) => {
  const {
    userId,
    jobId,
    evidenceId,
    casefileId,
    grantType,
    requestId = null,
    approvedBy = null,
    casefileName = 'Private Investigation',
    casefileType = 'private',
    showCasefileName = false
  } = grantData;

  // Validate required fields
  if (!userId || !jobId || !evidenceId || !casefileId || !grantType) {
    throw new Error('Missing required fields for access grant');
  }

  // Validate grant type
  const validTypes = ['owner', 'request_approved', 'purchase'];
  if (!validTypes.includes(grantType)) {
    throw new Error(`Invalid grant type: ${grantType}`);
  }

  // Create grant ID (composite key)
  const grantId = `${userId}_${jobId}`;

  // Check if grant already exists
  const existingGrant = await getDoc(doc(db, 'accessGrants', grantId));
  if (existingGrant.exists()) {
    const existing = existingGrant.data();
    if (existing.isActive) {
      console.log('Access grant already exists and is active');
      return grantId;
    }
    // If inactive, we'll overwrite (re-grant access)
  }

  // Determine if revocable
  const revocable = grantType === 'request_approved';

  // Build grant document
  const grant = {
    // Identity
    userId,
    jobId,
    evidenceId,
    casefileId,
    
    // Grant type
    grantType,
    isActive: true,
    revocable,
    
    // Permissions (standard set)
    permissions: {
      viewAnalysis: true,
      viewVideo: true,
      downloadVideo: false,
      exportData: true,
      screenshotFrames: true
    },
    
    // Timestamps
    createdAt: serverTimestamp(),
    expiresAt: null,  // Never expires (for now)
    
    // Context (cached for display)
    casefileName,
    casefileType,
    showCasefileName,
    
    // Request details (if applicable)
    requestId,
    approvedAt: requestId ? serverTimestamp() : null,
    approvedBy,
    
    // Purchase details (null for now - Phase 6+)
    purchasedAt: null,
    price: null,
    transactionId: null,
    
    // Revocation
    revokedAt: null,
    revokedBy: null,
    revokedReason: null
  };

  // Save to database
  try {
    await setDoc(doc(db, 'accessGrants', grantId), grant);
    console.log(`✅ Access grant created: ${grantId}`);
    return grantId;
  } catch (error) {
    console.error('Error creating access grant:', error);
    throw error;
  }
};

/**
 * Check if user has access to a job
 * 
 * @param {string} userId - User to check
 * @param {string} jobId - Job to check access for
 * @returns {Promise<Object>} Access status and details
 */
export const checkJobAccess = async (userId, jobId) => {
  if (!userId || !jobId) {
    return { 
      hasAccess: false, 
      reason: 'missing_params',
      error: 'userId and jobId are required'
    };
  }

  try {
    const grantId = `${userId}_${jobId}`;
    const grantDoc = await getDoc(doc(db, 'accessGrants', grantId));
    
    if (grantDoc.exists()) {
      const grant = grantDoc.data();
      
      // Check if active
      if (grant.isActive && !grant.revokedAt) {
        return {
          hasAccess: true,
          reason: grant.grantType,
          permissions: grant.permissions,
          grant: grant,
          grantId: grantId
        };
      } else {
        return {
          hasAccess: false,
          reason: 'revoked',
          revokedAt: grant.revokedAt,
          revokedReason: grant.revokedReason
        };
      }
    }
    
    return { 
      hasAccess: false, 
      reason: 'no_grant',
      message: 'No access grant found for this user and job'
    };
  } catch (error) {
    console.error('Error checking job access:', error);
    return { 
      hasAccess: false, 
      reason: 'error', 
      error: error.message 
    };
  }
};

/**
 * Revoke an access grant (only for revocable grants)
 * 
 * @param {string} grantId - Grant ID to revoke
 * @param {string} revokedBy - Who is revoking it
 * @param {string} [reason] - Why it's being revoked
 * @returns {Promise<boolean>} Success status
 */
export const revokeAccessGrant = async (grantId, revokedBy, reason = 'Access revoked by owner') => {
  if (!grantId || !revokedBy) {
    throw new Error('grantId and revokedBy are required');
  }

  try {
    const grantRef = doc(db, 'accessGrants', grantId);
    const grantDoc = await getDoc(grantRef);
    
    if (!grantDoc.exists()) {
      throw new Error('Grant not found');
    }
    
    const grant = grantDoc.data();
    
    // Check if revocable
    if (!grant.revocable) {
      throw new Error('This grant cannot be revoked (purchased license or ownership)');
    }
    
    // Check if already revoked
    if (!grant.isActive) {
      console.log('Grant is already revoked');
      return true;
    }
    
    // Revoke the grant (soft delete - keep for audit trail)
    await updateDoc(grantRef, {
      isActive: false,
      revokedAt: serverTimestamp(),
      revokedBy,
      revokedReason: reason
    });
    
    console.log(`✅ Access grant revoked: ${grantId}`);
    return true;
  } catch (error) {
    console.error('Error revoking access grant:', error);
    throw error;
  }
};

/**
 * Get all active grants for a user (for "My Licenses" page)
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Array>} List of grants
 */
export const getUserGrants = async (userId) => {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const grantsQuery = query(
      collection(db, 'accessGrants'),
      where('userId', '==', userId),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(grantsQuery);
    const grants = [];
    
    snapshot.forEach(doc => {
      grants.push({ 
        id: doc.id, 
        ...doc.data() 
      });
    });
    
    console.log(`Found ${grants.length} active grants for user ${userId}`);
    return grants;
  } catch (error) {
    console.error('Error getting user grants:', error);
    throw error;
  }
};

/**
 * Get all grants for a job (who has access to this job?)
 * 
 * @param {string} jobId - Job ID
 * @returns {Promise<Array>} List of grants
 */
export const getJobGrants = async (jobId) => {
  if (!jobId) {
    throw new Error('jobId is required');
  }

  try {
    const grantsQuery = query(
      collection(db, 'accessGrants'),
      where('jobId', '==', jobId),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(grantsQuery);
    const grants = [];
    
    snapshot.forEach(doc => {
      grants.push({ 
        id: doc.id, 
        ...doc.data() 
      });
    });
    
    console.log(`Found ${grants.length} active grants for job ${jobId}`);
    return grants;
  } catch (error) {
    console.error('Error getting job grants:', error);
    throw error;
  }
};

/**
 * Get a specific grant by ID
 * 
 * @param {string} grantId - Grant ID (userId_jobId)
 * @returns {Promise<Object|null>} Grant data or null
 */
export const getGrant = async (grantId) => {
  if (!grantId) {
    throw new Error('grantId is required');
  }

  try {
    const grantDoc = await getDoc(doc(db, 'accessGrants', grantId));
    
    if (grantDoc.exists()) {
      return {
        id: grantDoc.id,
        ...grantDoc.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting grant:', error);
    throw error;
  }
};

/**
 * Check if a grant exists (without fetching full data)
 * 
 * @param {string} userId - User ID
 * @param {string} jobId - Job ID
 * @returns {Promise<boolean>} True if grant exists
 */
export const grantExists = async (userId, jobId) => {
  const grantId = `${userId}_${jobId}`;
  
  try {
    const grantDoc = await getDoc(doc(db, 'accessGrants', grantId));
    return grantDoc.exists();
  } catch (error) {
    console.error('Error checking grant existence:', error);
    return false;
  }
};

/**
 * Get grant statistics for a user
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Grant statistics
 */
export const getUserGrantStats = async (userId) => {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const grantsQuery = query(
      collection(db, 'accessGrants'),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(grantsQuery);
    
    const stats = {
      total: 0,
      active: 0,
      revoked: 0,
      byType: {
        owner: 0,
        request_approved: 0,
        purchase: 0
      }
    };
    
    snapshot.forEach(doc => {
      const grant = doc.data();
      stats.total++;
      
      if (grant.isActive) {
        stats.active++;
      } else {
        stats.revoked++;
      }
      
      if (grant.grantType) {
        stats.byType[grant.grantType] = (stats.byType[grant.grantType] || 0) + 1;
      }
    });
    
    return stats;
  } catch (error) {
    console.error('Error getting grant stats:', error);
    throw error;
  }
};

// Export all functions
export default {
  createAccessGrant,
  checkJobAccess,
  revokeAccessGrant,
  getUserGrants,
  getJobGrants,
  getGrant,
  grantExists,
  getUserGrantStats
};

